/**
 * Detalle de una factura de Cuentas por Pagar.
 *
 * Mismo contrato y forma que el detalle de venta x mayor
 * (`facturadetallexmayor`): recibe el id, tiene su propia barra "Volver",
 * una cabecera de datos y pestañas. Antes el detalle era un diálogo pequeño
 * de solo lectura y para tocar los chasis había que salir a otra pantalla y
 * volver a teclear el número de factura.
 *
 * Pestañas: Chasis (con precios editables y devolución al suplidor), Pagos
 * (los recibos aplicados a esta factura) y Plan de pagos (cuotas con fecha;
 * ver planpagoscxp.js).
 *
 * Si la factura es una consignación, "Valor" es lo VENDIDO, no el conduce
 * entero: los motores en piso son del consignante y se le pueden devolver,
 * así que no se le deben. El total del conduce se muestra aparte.
 */
import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import SaveIcon from "@mui/icons-material/Save";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import TwoWheelerOutlinedIcon from "@mui/icons-material/TwoWheelerOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import { useMaterialUIController, useGlobalMDAlert } from "context";
import { puedeAccionPrograma, tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import {
  FP,
  fpInputSx,
  fpDataGridSx,
  StatusPill,
  RazonCancelacionTexto,
} from "components/financepro";
import { useApi } from "lib/api";
import { estadoConsignacion } from "lib/consignaciones";
import {
  imprimirFacturaCxp,
  imprimirDevolucionCxp,
} from "modules/cuentasxpagar/impresionfacturacxp";
import PlanPagosCxp from "modules/cuentasxpagar/planpagoscxp";

const toNum = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const fmtMoney = (v) =>
  toNum(v).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

// Pestañas tipo píldora (mismo estilo del detalle x mayor: facturadetallexmayor.js).
// El tema de la plantilla fuerza color dark con !important en el Tab activo,
// por eso el color blanco lo devuelve también con !important.
const tabSx = {
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.85rem",
  minHeight: 40,
  px: 2,
  borderRadius: 999,
  border: `1px solid ${FP.color.border}`,
  color: FP.color.textSecondary,
  bgcolor: "#FFFFFF",
  "& .MuiSvgIcon-root": { color: "inherit" },
  "&:hover": { borderColor: "#0B1220", color: "#0B1220" },
  "&.MuiTab-root.Mui-selected": {
    bgcolor: "#0B1220",
    borderColor: "#0B1220",
    color: "#FFFFFF !important",
  },
  "&.MuiTab-root.Mui-selected .MuiSvgIcon-root": {
    color: "#FFFFFF !important",
  },
};

function Dato({ label, value, strong = false }) {
  return (
    <Box>
      <Typography sx={{ fontSize: "0.68rem", color: FP.color.textMuted, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: strong ? "1.05rem" : "0.86rem",
          fontWeight: strong ? 800 : 600,
          color: FP.color.textPrimary,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function NoRowsOverlay({ texto }) {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>{texto}</Typography>
    </Stack>
  );
}

// El detalle trae `balance`; el helper de consignaciones razona con
// porpagar/sobrepago, que es el mismo número partido por signo.
function pillEstadoConsignada(factura) {
  const balance = toNum(factura.balance);
  const { label, variant } = estadoConsignacion(
    {
      anulada: factura.anulada,
      porpagar: Math.max(balance, 0),
      sobrepago: Math.max(-balance, 0),
      enpiso: factura.enpiso,
    },
    { femenino: true }
  );
  return { label, variant };
}

// Situación del chasis, derivada en el backend del detalle + inventario.
export function pillSituacion(situacion) {
  const s = String(situacion ?? "")
    .trim()
    .toUpperCase();
  if (s === "D") return <StatusPill label="Devuelto" variant="danger" />;
  if (s === "V") return <StatusPill label="Vendido" variant="neutral" />;
  if (s === "X") return <StatusPill label="Sin inventario" variant="info" />;
  return <StatusPill label="En inventario" variant="success" />;
}

export default function FacturaDetalleCxp({ facturaId, onExit, onChanged } = {}) {
  const [tab, setTab] = useState(0);
  const [factura, setFactura] = useState(null);
  const [lineas, setLineas] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [plan, setPlan] = useState(null);
  const [compania, setCompania] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState("");
  const [target, setTarget] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [devolviendo, setDevolviendo] = useState(false);

  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();

  // ares8550 gobierna los chasis de la factura (precio y devolución), igual
  // que en la pantalla independiente.
  const puedeChasis = tienePermisosModulo(roleDetalles, "ares8550");
  const puedePrecios = puedeChasis && puedeAccionPrograma(roleDetalles, "ares8550", "modificar");
  const puedeDevolver = puedeChasis && puedeAccionPrograma(roleDetalles, "ares8550", "borrar");
  // El plan de pagos se arma desde Mantenimiento de Facturas con su flag modificar.
  const puedePlan = puedeAccionPrograma(roleDetalles, "ares8520", "modificar");

  async function cargar() {
    setCargando(true);
    setError("");
    try {
      const { status, data } = await post("/api/cxp_factura_detalle", { id: facturaId });
      if (status === 200) {
        setFactura(data.factura || null);
        setPagos(data.pagos || []);
        setPlan(data.plan || null);
        setCompania(data.compania || null);
        setLineas(
          (data.lineas || []).map((l) => ({
            ...l,
            id: l.chasis,
            costo: String(toNum(l.valor)),
            precioventa: String(toNum(l.precio)),
            preciominimo: String(toNum(l.mprecio)),
          }))
        );
      } else {
        setFactura(null);
        setLineas([]);
        setPagos([]);
        setPlan(null);
        setError(
          data?.Error ||
            (status === 403
              ? "Su rol no tiene acceso a esta factura"
              : `No se pudo cargar la factura (HTTP ${status})`)
        );
      }
    } catch (err) {
      setError(`No se pudo contactar el servidor: ${err?.message || err}`);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facturaId]);

  const setCampo = (chasis, campo, valor) =>
    setLineas((prev) => prev.map((l) => (l.chasis === chasis ? { ...l, [campo]: valor } : l)));

  const guardarPrecio = async (row) => {
    if (toNum(row.costo) < 0 || toNum(row.precioventa) < 0 || toNum(row.preciominimo) < 0) {
      showAlert({ message: "Los valores no pueden ser negativos", color: "warning" });
      return;
    }
    setGuardando(row.chasis);
    try {
      const { ok, data } = await post("/api/cxp_chasis_precio", {
        id: factura.id,
        chasis: row.chasis,
        valor: toNum(row.costo),
        precio: toNum(row.precioventa),
        mprecio: toNum(row.preciominimo),
      });
      if (ok) {
        showAlert({
          message: `${data.ok || "Precios guardados"}. Nuevo total RD$ ${fmtMoney(data.total)}`,
          color: "success",
        });
        cargar();
        if (onChanged) onChanged();
      } else {
        showAlert({ message: data.Error || "No se pudieron guardar los precios", color: "error" });
      }
    } finally {
      setGuardando("");
    }
  };

  const devolverChasis = async () => {
    setDevolviendo(true);
    try {
      const { ok, data } = await post("/api/cxp_chasis_eliminar", {
        id: factura.id,
        chasis: target.chasis,
        motivo: motivo.trim(),
      });
      if (ok) {
        setTarget(null);
        setMotivo("");
        showAlert({
          message: `${data.ok || "Devolución registrada"}. Nuevo total RD$ ${fmtMoney(data.total)}`,
          color: "success",
        });
        if (data.devolucion) imprimirDevolucionCxp(data.devolucion, compania);
        cargar();
        if (onChanged) onChanged();
      } else {
        showAlert({ message: data.Error || "No se pudo devolver el chasis", color: "error" });
      }
    } finally {
      setDevolviendo(false);
    }
  };

  const anulada = !!factura?.anulada;
  // En una consignada la factura solo la componen los motores vendidos, y
  // sobre un motor vendido no se puede ni cambiar el costo ni devolverlo: el
  // backend los rechaza. Ofrecer los botones sería ofrecer un 400.
  const soloLectura = anulada || !!factura?.consignada;

  const campoNumero = (row, campo, editable) =>
    editable ? (
      <TextField
        size="small"
        type="number"
        value={row[campo]}
        onChange={(e) => setCampo(row.chasis, campo, e.target.value)}
        inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }}
        sx={{ ...fpInputSx, width: "100%" }}
      />
    ) : (
      <span>{fmtMoney(row[campo])}</span>
    );

  const columnasLineas = useMemo(
    () => [
      { field: "chasis", headerName: "Chasis", minWidth: 170, flex: 1 },
      { field: "marca", headerName: "Marca", width: 110 },
      { field: "modelo", headerName: "Modelo", width: 110 },
      { field: "colore", headerName: "Color", width: 100 },
      {
        field: "situacion",
        headerName: "Situación",
        width: 140,
        sortable: false,
        renderCell: (params) => pillSituacion(params.value),
      },
      {
        field: "costo",
        headerName: "Costo",
        width: 130,
        align: "right",
        headerAlign: "right",
        sortable: false,
        renderCell: (params) =>
          campoNumero(
            params.row,
            "costo",
            puedePrecios && !soloLectura && params.row.situacion !== "D"
          ),
      },
      {
        field: "precioventa",
        headerName: "Precio venta",
        width: 140,
        align: "right",
        headerAlign: "right",
        sortable: false,
        renderCell: (params) =>
          campoNumero(
            params.row,
            "precioventa",
            puedePrecios && !soloLectura && params.row.situacion !== "D"
          ),
      },
      {
        field: "preciominimo",
        headerName: "Precio mínimo",
        width: 140,
        align: "right",
        headerAlign: "right",
        sortable: false,
        renderCell: (params) =>
          campoNumero(
            params.row,
            "preciominimo",
            puedePrecios && !soloLectura && params.row.situacion !== "D"
          ),
      },
      {
        field: "acciones",
        headerName: "",
        width: 210,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          if (params.row.situacion === "D") {
            return (
              <Typography sx={{ color: FP.color.textMuted, fontSize: "0.72rem", fontWeight: 700 }}>
                Devuelto
              </Typography>
            );
          }
          return (
            <Stack direction="row" spacing={0.8}>
              {puedePrecios && !soloLectura && (
                <Button
                  size="small"
                  startIcon={
                    guardando === params.row.chasis ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={() => guardarPrecio(params.row)}
                  disabled={guardando === params.row.chasis}
                  sx={btnOutline(FP.color.navy)}
                >
                  Guardar
                </Button>
              )}
              {puedeDevolver && !soloLectura && (
                <Button
                  size="small"
                  startIcon={<AssignmentReturnOutlinedIcon />}
                  onClick={() => setTarget(params.row)}
                  sx={btnOutline(FP.color.danger)}
                >
                  Devolver
                </Button>
              )}
            </Stack>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [puedePrecios, puedeDevolver, soloLectura, guardando, lineas]
  );

  const columnasPagos = useMemo(
    () => [
      { field: "id", headerName: "Pago#", width: 100 },
      { field: "fecha", headerName: "Fecha", width: 110 },
      { field: "tipodepago", headerName: "Tipo", width: 140 },
      { field: "banco", headerName: "Banco", width: 130 },
      { field: "referencia", headerName: "Referencia", width: 140 },
      { field: "descripcion", headerName: "Descripción", flex: 1, minWidth: 180 },
      {
        field: "valorpagado",
        headerName: "Valor",
        width: 130,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => fmtMoney(value),
      },
      {
        field: "anulado",
        headerName: "Estado",
        width: 120,
        sortable: false,
        renderCell: (params) =>
          params.value ? (
            <StatusPill label="Anulado" variant="danger" />
          ) : (
            <StatusPill label="Aplicado" variant="success" />
          ),
      },
    ],
    []
  );

  if (cargando) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
        <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }} spacing={1.5}>
          <CircularProgress size={26} />
          <Typography sx={{ color: FP.color.textSecondary }}>Cargando factura…</Typography>
        </Stack>
      </Box>
    );
  }

  if (error || !factura) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
        <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }} spacing={1.5}>
          <Typography sx={{ color: FP.color.danger, fontWeight: 700 }}>
            {error || "No se encontró la factura"}
          </Typography>
          <Stack direction="row" spacing={1.2}>
            <Button onClick={cargar} sx={btnOutline(FP.color.navy)}>
              Reintentar
            </Button>
            <Button onClick={onExit} sx={btnOutline(FP.color.textSecondary)}>
              Volver
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.4,
          bgcolor: FP.color.cardBg,
          borderBottom: `1px solid ${FP.color.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onExit}
            sx={{ textTransform: "none", fontWeight: 700, color: FP.color.textPrimary }}
          >
            Volver
          </Button>
          <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "1.05rem" }}>
            Factura CxP #{factura.nfactura}
          </Typography>
          {anulada && <StatusPill label="Anulada" variant="danger" />}
          {anulada && <RazonCancelacionTexto razon={factura.razonanula} />}
          {factura.consignada && <StatusPill label="Consignado" variant="info" />}
          {/* En una consignada, sin saldo NO es lo mismo que saldada: cada
              motor que quede en piso puede venderse y volver a generar
              deuda. Lo decide el mismo helper que el resto de pantallas. */}
          {!anulada &&
            (factura.consignada ? (
              <StatusPill {...pillEstadoConsignada(factura)} />
            ) : (
              toNum(factura.balance) <= 0 && <StatusPill label="Saldada" variant="success" />
            ))}
        </Box>
        <Button
          startIcon={<PrintOutlinedIcon />}
          onClick={() => imprimirFacturaCxp({ factura, lineas, compania })}
          sx={btnOutline(FP.color.navy)}
        >
          Imprimir
        </Button>
      </Box>

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ ...sectionBox, mb: 2.5 }}>
          <Grid container spacing={2.4}>
            <Grid item xs={12} sm={4} md={3}>
              <Dato label="SUPLIDOR" value={factura.suplidornombre || "—"} />
            </Grid>
            <Grid item xs={6} sm={3} md={1.6}>
              <Dato label="RNC" value={factura.suplidorrnc || "—"} />
            </Grid>
            <Grid item xs={6} sm={2} md={1.4}>
              <Dato label="FECHA" value={factura.fecha || "—"} />
            </Grid>
            <Grid item xs={6} sm={3} md={1.6}>
              <Dato label="TIPO" value={factura.tipofactura || "—"} />
            </Grid>
            <Grid item xs={6} sm={4} md={1.8}>
              <Dato label="NCF" value={factura.datafiscal || "—"} />
            </Grid>
            <Grid item xs={4} sm={2} md={1.5}>
              <Dato
                label={factura.consignada ? "VALOR VENDIDO" : "VALOR"}
                value={fmtMoney(factura.valor)}
                strong
              />
            </Grid>
            <Grid item xs={4} sm={2} md={1.5}>
              <Dato label="PAGADO" value={fmtMoney(factura.vplicado)} strong />
            </Grid>
            <Grid item xs={4} sm={2} md={1.5}>
              <Dato label="BALANCE" value={fmtMoney(factura.balance)} strong />
            </Grid>
            {factura.consignada && (
              <>
                <Grid item xs={6} sm={3} md={2}>
                  <Dato label="TOTAL DEL CONDUCE" value={fmtMoney(factura.valorconduce)} />
                </Grid>
                <Grid item xs={3} sm={2} md={1.4}>
                  <Dato label="EN PISO" value={String(factura.enpiso ?? 0)} />
                </Grid>
                <Grid item xs={3} sm={2} md={1.4}>
                  <Dato label="VENDIDOS" value={String(factura.vendidos ?? 0)} />
                </Grid>
              </>
            )}
            {factura.concepto ? (
              <Grid item xs={12}>
                <Dato label="CONCEPTO" value={factura.concepto} />
              </Grid>
            ) : null}
          </Grid>
          {factura.consignada && (
            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.8rem", mt: 1.6 }}>
              Es una consignación: solo se debe lo vendido. Los {factura.enpiso ?? 0} motores en
              piso son del consignante y se le pueden devolver, así que no entran en el balance.
            </Typography>
          )}
        </Box>

        <Box sx={sectionBox}>
          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v)}
            variant="scrollable"
            allowScrollButtonsMobile
            scrollButtons={false}
            TabIndicatorProps={{ sx: { display: "none" } }}
            sx={{
              mb: 2.4,
              minHeight: 44,
              "& .MuiTabs-flexContainer": { gap: 1, width: "100%" },
              "& .MuiTab-root": { flex: { md: 1 }, maxWidth: "none" },
            }}
          >
            <Tab
              icon={<TwoWheelerOutlinedIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={
                factura.consignada
                  ? `Chasis vendidos (${lineas.length})`
                  : `Chasis (${lineas.length})`
              }
              sx={tabSx}
            />
            <Tab
              icon={<HistoryOutlinedIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={`Pagos (${pagos.length})`}
              sx={tabSx}
            />
            <Tab
              icon={<EventNoteOutlinedIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={
                plan
                  ? `Plan de pagos (${plan.cuotas.length})${
                      toNum(plan.vencido) > 0 ? " · vencido" : ""
                    }`
                  : "Plan de pagos"
              }
              sx={tabSx}
            />
          </Tabs>

          {tab === 0 ? (
            <Box sx={{ bgcolor: "#fff" }}>
              <DataGrid
                autoHeight
                rows={lineas}
                columns={columnasLineas}
                getRowId={(r) => r.chasis}
                rowHeight={52}
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                slots={{
                  noRowsOverlay: () => <NoRowsOverlay texto="La factura no tiene chasis" />,
                }}
                sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "200px" }}
                disableRowSelectionOnClick
              />
              {factura.consignada && (
                <Typography
                  sx={{ color: FP.color.textSecondary, fontSize: "0.8rem", mt: 1.4, px: 0.5 }}
                >
                  Solo los motores vendidos: son los que componen esta cuenta por pagar y suman su
                  valor. Los {factura.enpiso ?? 0} que siguen en piso y los {factura.devueltos ?? 0}{" "}
                  devueltos se ven, y se devuelven, en el módulo de Consignaciones.
                </Typography>
              )}
            </Box>
          ) : tab === 2 ? (
            <PlanPagosCxp
              factura={factura}
              plan={plan}
              puedeEditar={puedePlan}
              onCambio={() => {
                cargar();
                if (onChanged) onChanged();
              }}
            />
          ) : (
            <Box sx={{ bgcolor: "#fff" }}>
              <DataGrid
                autoHeight
                rows={pagos}
                columns={columnasPagos}
                getRowId={(r) => r.id}
                rowHeight={44}
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                slots={{
                  noRowsOverlay: () => (
                    <NoRowsOverlay texto="Esta factura no tiene pagos registrados" />
                  ),
                }}
                sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "200px" }}
                disableRowSelectionOnClick
              />
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        open={!!target}
        onClose={() => (devolviendo ? null : setTarget(null))}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Devolver chasis al suplidor</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.92rem" }}>
            El chasis {target?.chasis} saldrá de la factura y del inventario, y se descontará su
            valor del total. La devolución queda registrada y se imprime su comprobante.
          </Typography>
          <TextField
            fullWidth
            label="Motivo de la devolución"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            disabled={devolviendo}
            sx={{ ...fpInputSx, mt: 2 }}
            InputLabelProps={{ shrink: true }}
          />
          <DialogActions sx={{ mt: 2, px: 0 }}>
            <Button
              onClick={() => setTarget(null)}
              disabled={devolviendo}
              sx={btnOutline(FP.color.navy)}
            >
              Cancelar
            </Button>
            <Button
              startIcon={
                devolviendo ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AssignmentReturnOutlinedIcon />
                )
              }
              onClick={devolverChasis}
              disabled={devolviendo}
              sx={btnSolid(FP.color.danger, FP.color.danger)}
            >
              {devolviendo ? "Devolviendo…" : "Devolver"}
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
